import { faCamera } from '@fortawesome/pro-light-svg-icons/faCamera'
import Icon from '@space-core/components/icon'
import Image from '@space-core/components/image'
import productDefaultImage from '@space-shell/assets/images/productDefaultImage.svg'
import { ShowcaseOrientation } from '@space-shell/common/services/membership'
import clsx from 'clsx'
import styled from 'styled-components'

type ThumbnailProps = {
  className?: string
  title: string
  image?: {
    url?: string
    altText?: string
  }
  verticalImage?: {
    url?: string
    altText?: string
  }
  horizontalImage?: {
    url?: string
    altText?: string
  }
  avatar?: {
    url?: string
    altText?: string
  }
  bwImage?: {
    url?: string
    altText?: string
  }
  orientation?: ShowcaseOrientation
  version?: string
}

interface StyledAvatarProps {
  fetchpriority?: 'high' | 'low' | 'auto'
  $orientation?: ShowcaseOrientation
}

const Blur = styled.div<{ $background: string }>`
  transform: scale(1.3);
  filter: blur(1.563rem);
  -webkit-filter: blur(1.563rem);
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  background-repeat: no-repeat;
  background: linear-gradient(rgba(25, 28, 31, 0.5), rgba(25, 28, 31, 0.5)),
    ${({ $background }) => `url('${$background}')`};
`

export const Avatar = styled(Image)<StyledAvatarProps>`
  top: 50%;
  left: 50%;
  z-index: 2;
  transform: translate(-50%, -50%);
  height: ${({ $orientation }) =>
    $orientation === ShowcaseOrientation.HORIZONTAL ? `100%` : `65%`};
  width: ${({ $orientation }) =>
    $orientation === ShowcaseOrientation.HORIZONTAL ? `65%` : `100%`};
`

const Thumbnail = ({
  image,
  avatar,
  verticalImage,
  horizontalImage,
  bwImage,
  orientation,
  title,
  version,
  className
}: ThumbnailProps) => {
  const avatarThumbnail = avatar?.url && !version ? avatar : bwImage

  const thumbnail = () => {
    if (!version) {
      if (orientation === ShowcaseOrientation.VERTICAL) {
        return verticalImage || image
      }

      return horizontalImage || image
    }

    if (orientation === ShowcaseOrientation.VERTICAL) {
      return verticalImage
    }

    return horizontalImage
  }

  if (!thumbnail()?.url && !avatarThumbnail?.url) {
    return (
      <Icon
        aria-hidden
        icon={faCamera}
        data-test='empty-cover'
        className='_text-gray-400 _text-8'
      />
    )
  }

  if (!thumbnail()?.url && avatarThumbnail?.url) {
    return (
      <div className='_position-relative _bg-gray-200 _w-full _h-full'>
        <Avatar
          aria-hidden
          src={avatarThumbnail.url}
          objectFit='cover'
          objectPosition='center'
          fetchpriority='low'
          decoding='async'
          $orientation={orientation}
          alt={avatarThumbnail.altText || title}
          errorSrc={productDefaultImage}
          className={clsx('_position-absolute', className)}
        />

        <Blur $background={avatarThumbnail.url} className='_w-full _h-full' />
      </div>
    )
  }

  return (
    <Image
      aria-hidden
      objectFit='cover'
      src={thumbnail()?.url}
      objectPosition='center'
      alt={thumbnail()?.altText || title}
      errorSrc={productDefaultImage}
      className={clsx('_w-full _h-full', className)}
    />
  )
}

export default Thumbnail
